import {
    convertToDate,
    isAfter,
    isBefore,
    isBetween,
    isValidDate,
    isValidTime,
} from '@shared/helpers/datetime/datetime';
import { ValidationResult } from './Validations.interfaces';
import { t } from '@shared/helpers/translation';
import { isValidPhoneNumber } from '@shared/helpers/validation';
import { isBoolean, isEmpty, isNil, isObject, isString, uniq } from 'lodash';

export const isRequired = (value: any): ValidationResult => {
    return value && value !== ' ' ? true : t({ key: 'validation.required' });
};

export const isDefined = (value: any): ValidationResult => {
    return value !== undefined && value !== null ? true : t({ key: 'validation.defined' });
};

export const isDefinedObject = (value: any): ValidationResult => {
    const isValidObject = isObject(value) ? Object.keys(value).length > 0 : false;

    return isValidObject ? true : t({ key: 'validation.defined' });
};

export const isDefinedBoolean = (value: any): ValidationResult => {
    return isBoolean(value) ? true : t({ key: 'validation.defined' });
};

export const isSelected = (value: any): ValidationResult => {
    return value !== undefined && value !== null && value !== 0
        ? true
        : t({ key: 'validation.selected' });
};

export const isInteger = (value: any): ValidationResult => {
    const parsedValue = typeof value === 'string' ? Number(value) : value;

    return Number.isInteger(parsedValue) ? true : t({ key: 'validation.integer' });
};

export const isPositiveInteger = (value: any): ValidationResult => {
    const parsedValue = typeof value === 'string' ? Number(value) : value;

    return Number.isInteger(parsedValue) && parsedValue > 0
        ? true
        : t({ key: 'validation.integer' });
};

export const isDecimal = (value: any): ValidationResult => {
    const regex = /^-?\d+(\.\d+)?$/;

    return regex.test(value) ? true : t({ key: 'validation.decimal' });
};

export const isEmail = (value: any): ValidationResult => {
    const regex = new RegExp(`^(\\w{1}[\\w\\.\\-_]+@[\\w\\-]+\\.\\w+[\\.\\w]*)$`);
    return regex.test(value) ? true : t({ key: 'validation.email' });
};

export const isName = (value: any): ValidationResult => {
    const chars = '±!@£$%^&*_+§¡€#¢¶¤•ªº«<>?:\\/;=.,0-9%&';
    const regex = new RegExp(`^(?!.*\\s{2})^[^\\s${chars}]{2,}(?:\\s+[^${chars}]+)*$`);
    return regex.test(value) ? true : t({ key: 'validation.name' });
};

export const isValidCharacters = (value: any): ValidationResult => {
    if (isString(value) && isEmpty(value)) {
        return true;
    }

    const chars = '±^*§¡€¢¶¤•ªº«<>\\;';
    const regex = new RegExp(`^(?!.*\\s{2})^[^\\s${chars}]+(?:\\s+[^${chars}]+)*$`);

    const invalidRegex = new RegExp(`[${chars}]`, 'g');
    const invalidCharsMatched = value ? uniq(value.match(invalidRegex)) : [];

    return regex.test(value)
        ? true
        : t({
              key: 'validation.valid_characters',
              values: [invalidCharsMatched.join(', ')],
          });
};

export const isLicensePlate = (value: any): ValidationResult => {
    const chars = '±!@£$%^&*_+§¡€#¢¶¤•ªº«<>?:\\/;=,';
    const regex = new RegExp(`^(?!.*\\s{2,})[^\\s${chars}]+[^${chars}]*[^\\s${chars}]+$`);

    return regex.test(value) ? true : t({ key: 'validation.license_plate' });
};

export const isPassportNumber = (value: any): ValidationResult => {
    const chars = '±!@£$%^&*_+§¡€#¢¶¤•ªº«<>?:\\/\\|;=.,';
    const regex = new RegExp(`^[^\\s${chars}]+[^${chars}]*[^\\s${chars}]+$`);

    return regex.test(value) ? true : t({ key: 'validation.passport_number' });
};

export const isFlightNumber = (value: any): ValidationResult => {
    const regex = new RegExp(`^[A-Z0-9]{2,3}[-\\s]?\\d{1,5}[A-Z]?$`, 'i');
    return regex.test(value) ? true : t({ key: 'validation.flight_number' });
};

export const isPersonalIdNumber = (value: any): ValidationResult => {
    const chars = '±!@£$%^&*_+§¡€#¢¶¤•ªº«<>?:\\/\\|;=.,';
    const regex = new RegExp(`^[^\\s${chars}]+[^${chars}]*[^\\s${chars}]+$`);

    return regex.test(value) ? true : t({ key: 'validation.personal_id_number' });
};

export const isMembershipNumber =
    (length: number) =>
    (value: any): ValidationResult => {
        if ((isString(value) && isEmpty(value)) || isNil(value)) {
            return true;
        }

        const regex = new RegExp(`^[0-9]{${length}}$`);

        return regex.test(value)
            ? true
            : t({ key: 'validation.membership_number', values: [length] });
    };

export const isDate = (value: any): ValidationResult => {
    return value && isValidDate(new Date(value)) ? true : t({ key: 'validation.date' });
};

export const isTime = (value: any): ValidationResult => {
    return value && isValidTime(value) ? true : t({ key: 'validation.time' });
};

export const isDateBetween =
    (d1: Date, d2: Date) =>
    (value: any): ValidationResult => {
        if (!value || value.length !== 10) {
            return t({ key: 'validation.date' });
        }

        const date = convertToDate(value);
        return isValidDate(date) && isBetween(date, d1, d2)
            ? true
            : t({ key: 'validation.date' });
    };

export const isDateBefore =
    (d1: Date) =>
    (value: any): ValidationResult => {
        if (!value || value.length !== 10) {
            return t({ key: 'validation.date' });
        }

        const date = convertToDate(value);
        return isValidDate(date) && isBefore(date, d1) ? true : t({ key: 'validation.date' });
    };

export const isDateAfter =
    (d1: Date, message?: string) =>
    (value: any): ValidationResult => {
        if (!value || value.length !== 10) {
            return t({ key: 'validation.date' });
        }

        const date = convertToDate(value);
        return isValidDate(date) && isAfter(date, d1)
            ? true
            : (message ?? t({ key: 'validation.date' }));
    };

export const isPhoneNumber = (value: any): ValidationResult => {
    return value && isValidPhoneNumber(value) ? true : t({ key: 'validation.phone' });
};

const luhnCheck = (value: string): boolean => {
    const arr = value
        .split('')
        .reverse()
        .map((x) => Number.parseInt(x, 10));

    const lastDigit = arr.shift() ?? 0;

    let sum = arr.reduce((acc, val, i) => {
        if (i % 2 !== 0) {
            return acc + val;
        }

        let doubled = val * 2;
        if (doubled > 9) doubled -= 9;

        return acc + doubled;
    }, 0);

    sum += lastDigit;

    return sum % 10 === 0;
};

export const isValidSwedishPersonnummer = (value: any): ValidationResult => {
    const validationMessage = t({ key: 'validation.leabank_swedish_personal_id_number' });
    const str = String(value).trim();
    const birthDate = str.slice(0, 8);

    // Check for length and valid date
    if (!/^\d{12}$/.test(str) || !isValidDate(birthDate)) {
        return validationMessage;
    }

    const luhnPart = str.slice(2); // Use only YYMMDDNNNX (last 10 digits)
    const result = luhnCheck(luhnPart);

    return result ? result : validationMessage;
};
